import { featureFlagsAtom } from '@polaris/core/stores'
import { useAtomValue } from 'jotai'
import { X } from 'lucide-react'
import { motion } from 'motion/react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useKeyPressEvent } from 'react-use'
import { useDebounce } from 'use-debounce'
import { useSupportedChains } from '@/src/queries/hooks/chains'
import { useTokenSelection } from '@/src/stores/token-selection/actions'
import { FRAMER_MOTION_TOKEN_SELECT_EASING } from '@/src/utils/consts'
import { cn } from '@/src/utils/styles'
import { Button } from '../../ui/button'
import { SearchField } from '../../ui/search-field'
import { NetworkRows } from './components'
import type { ChainToken } from './types'

export const FilterNetworkSelection = ({
  isOpen,
  onRequestClose,
}: {
  isOpen: boolean
  onRequestClose: () => void
}) => {
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounce(query, 300)
  const { setFilteredNetwork } = useTokenSelection()
  const ref = useRef<HTMLInputElement>(null)
  const [hasFocused, setHasFocused] = useState(false)

  const { data: supportedChains, isLoading } = useSupportedChains()

  const featureFlags = useAtomValue(featureFlagsAtom)
  const privacyUiEnabled = featureFlags.privacyUi

  const filteredChains = useMemo(() => {
    const filtered =
      supportedChains?.filter((chain) =>
        chain.chain_name.toLowerCase().includes(debouncedQuery.toLowerCase()),
      ) ?? []

    // If privacy UI is enabled, sort Privacy Vault to the top
    if (privacyUiEnabled) {
      return [...filtered].sort((a, b) => {
        const aIsPrivacy = a.chain_id.includes('namada')
        const bIsPrivacy = b.chain_id.includes('namada')

        if (aIsPrivacy && !bIsPrivacy) return -1
        if (!aIsPrivacy && bIsPrivacy) return 1
        return 0
      })
    }

    return filtered
  }, [debouncedQuery, supportedChains, privacyUiEnabled])

  const handleSelectNetwork = useCallback(
    (chain: ChainToken | null) => {
      setFilteredNetwork(chain)
      onRequestClose()
    },
    [onRequestClose, setFilteredNetwork],
  )

  const [isDelayedOpen] = useDebounce(isOpen, 300)

  useKeyPressEvent('Escape', () => {
    if (isOpen) {
      onRequestClose()
    }
  })

  return (
    <motion.div
      className={cn('absolute inset-0 flex flex-col bg-background', {
        hidden: !isOpen && !isDelayedOpen,
      })}
      onAnimationComplete={() => {
        if (!isOpen) {
          setHasFocused(false)
          setQuery('')
        }
        if (isOpen && ref.current && !hasFocused) {
          ref.current.focus()
          setHasFocused(true)
        }
      }}
      animate={{ y: isOpen ? 0 : '100%' }}
      initial={{ y: '100%' }}
      exit={{ y: '100%' }}
      transition={{ duration: 0.3, ease: FRAMER_MOTION_TOKEN_SELECT_EASING }}
    >
      <div className='flex items-center gap-3 px-6 pt-6 pb-2'>
        <SearchField
          dimension='medium'
          data-testid='search-filter-network'
          inputProps={{
            placeholder: 'Search networks',
            value: query,
            onChange: (e) => setQuery(e.target.value),
            ref,
          }}
          resetState={() => setQuery('')}
          className='w-full'
        />
        <Button type='button' size='md' onClick={onRequestClose}>
          <X className='size-6 link' />
        </Button>
      </div>
      <NetworkRows
        filteredChains={filteredChains}
        isLoading={isLoading}
        query={debouncedQuery}
        onSelectNetwork={handleSelectNetwork}
        header='Show tokens on'
        showTooltips={false}
      />
    </motion.div>
  )
}
